/**
 * QR encoder ขนาดเล็ก (byte mode, ECC level M, version 1–10)
 *
 * ทำไมเขียนเอง: จอ 0 ต้องหมุน nonce ทุก 60 วิ แล้ว render QR ใหม่ ซึ่งเป็น
 * ความสามารถหลักของงาน แต่กฎในโปรเจกต์ห้ามเพิ่ม dependency ที่ไม่ใช่ dev
 * โดยไม่ถามก่อน — QR byte mode เป็นสเปกปิดที่เขียนเองได้ในไม่กี่ร้อยบรรทัด
 *
 * URL ที่ต้องเข้ารหัสคือ https://<mobile>/r/0?n=xxxx ซึ่งสั้นมาก
 * version 3 (29×29) ที่ ECC M รับได้ 62 ไบต์ จึงเหลือเฟือ
 */

const GF_EXP = new Uint8Array(512);
const GF_LOG = new Uint8Array(256);
{
  let x = 1;
  for (let i = 0; i < 255; i++) {
    GF_EXP[i] = x;
    GF_LOG[x] = i;
    x <<= 1;
    if (x & 0x100) x ^= 0x11d;
  }
  for (let i = 255; i < 512; i++) GF_EXP[i] = GF_EXP[i - 255]!;
}

const gfMul = (a: number, b: number): number =>
  a === 0 || b === 0 ? 0 : GF_EXP[GF_LOG[a]! + GF_LOG[b]!]!;

function rsGenerator(degree: number): Uint8Array {
  let poly = new Uint8Array([1]);
  for (let i = 0; i < degree; i++) {
    const next = new Uint8Array(poly.length + 1);
    for (let j = 0; j < poly.length; j++) {
      next[j] = (next[j] ?? 0) ^ (poly[j] ?? 0);
      next[j + 1] = (next[j + 1] ?? 0) ^ gfMul(poly[j] ?? 0, GF_EXP[i] ?? 0);
    }
    poly = next;
  }
  return poly;
}

function rsEncode(data: Uint8Array, ecLen: number): Uint8Array {
  const gen = rsGenerator(ecLen);
  const res = new Uint8Array(ecLen);
  for (const byte of data) {
    const factor = byte ^ (res[0] ?? 0);
    res.copyWithin(0, 1);
    res[ecLen - 1] = 0;
    for (let i = 0; i < ecLen; i++) res[i] = (res[i] ?? 0) ^ gfMul(gen[i + 1] ?? 0, factor);
  }
  return res;
}

/** ตาราง ECC level M: [จำนวน codeword ทั้งหมด, ec ต่อบล็อก, กลุ่ม1, กลุ่ม2] */
const VERSION_M: Record<number, { total: number; ec: number; blocks: number[] }> = {
  1: { total: 26, ec: 10, blocks: [16] },
  2: { total: 44, ec: 16, blocks: [28] },
  3: { total: 70, ec: 26, blocks: [44] },
  4: { total: 100, ec: 18, blocks: [32, 32] },
  5: { total: 134, ec: 24, blocks: [43, 43] },
  6: { total: 172, ec: 16, blocks: [27, 27, 27, 27] },
  7: { total: 196, ec: 18, blocks: [31, 31, 31, 31] },
  8: { total: 242, ec: 22, blocks: [38, 38, 38, 38] },
  9: { total: 292, ec: 22, blocks: [36, 36, 36, 36, 37] },
  10: { total: 346, ec: 26, blocks: [43, 43, 43, 43, 44, 44] },
};

const ALIGN_POS: Record<number, number[]> = {
  1: [], 2: [6, 18], 3: [6, 22], 4: [6, 26], 5: [6, 30],
  6: [6, 34], 7: [6, 22, 38], 8: [6, 24, 42], 9: [6, 26, 46], 10: [6, 28, 50],
};

function pickVersion(byteLen: number): number {
  for (let v = 1; v <= 10; v++) {
    const spec = VERSION_M[v]!;
    const dataCap = spec.blocks.reduce((a, b) => a + b, 0);
    const lenBits = v <= 9 ? 8 : 16;
    if (dataCap * 8 >= 4 + lenBits + byteLen * 8) return v;
  }
  throw new Error("ข้อความยาวเกินกว่า QR version 10 ที่ ECC M จะรับได้");
}

function buildData(text: string, version: number): Uint8Array {
  const bytes = new TextEncoder().encode(text);
  const spec = VERSION_M[version]!;
  const dataCap = spec.blocks.reduce((a, b) => a + b, 0);

  const bits: number[] = [];
  const push = (val: number, len: number) => {
    for (let i = len - 1; i >= 0; i--) bits.push((val >> i) & 1);
  };

  push(0b0100, 4); // byte mode
  push(bytes.length, version <= 9 ? 8 : 16);
  for (const b of bytes) push(b, 8);

  const capBits = dataCap * 8;
  for (let i = 0; i < 4 && bits.length < capBits; i++) bits.push(0); // terminator
  while (bits.length % 8 !== 0) bits.push(0);

  const out = new Uint8Array(dataCap);
  for (let i = 0; i < bits.length / 8; i++) {
    let v = 0;
    for (let j = 0; j < 8; j++) v = (v << 1) | bits[i * 8 + j]!;
    out[i] = v;
  }
  // pad bytes สลับ 0xEC / 0x11 ตามสเปก
  for (let i = bits.length / 8; i < dataCap; i++) {
    out[i] = (i - bits.length / 8) % 2 === 0 ? 0xec : 0x11;
  }
  return out;
}

function interleave(data: Uint8Array, version: number): Uint8Array {
  const spec = VERSION_M[version]!;
  const blocks: Uint8Array[] = [];
  const ecs: Uint8Array[] = [];
  let off = 0;
  for (const size of spec.blocks) {
    const chunk = data.subarray(off, off + size);
    off += size;
    blocks.push(chunk);
    ecs.push(rsEncode(chunk, spec.ec));
  }

  const out: number[] = [];
  const maxData = Math.max(...spec.blocks);
  for (let i = 0; i < maxData; i++) {
    for (const b of blocks) if (i < b.length) out.push(b[i]!);
  }
  for (let i = 0; i < spec.ec; i++) {
    for (const e of ecs) out.push(e[i]!);
  }
  return new Uint8Array(out);
}

type Grid = { size: number; mod: Int8Array }; // -1 = ยังว่าง, 0/1 = สี

function idx(g: Grid, x: number, y: number): number {
  return y * g.size + x;
}

function placeFunctionPatterns(g: Grid, version: number): void {
  const n = g.size;
  const set = (x: number, y: number, v: number) => {
    if (x >= 0 && y >= 0 && x < n && y < n) g.mod[idx(g, x, y)] = v as -1 | 0 | 1;
  };

  // finder + separator
  for (const [ox, oy] of [[0, 0], [n - 7, 0], [0, n - 7]] as const) {
    for (let dy = -1; dy <= 7; dy++) {
      for (let dx = -1; dx <= 7; dx++) {
        const inRing =
          dx >= 0 && dx <= 6 && dy >= 0 && dy <= 6 &&
          (dx === 0 || dx === 6 || dy === 0 || dy === 6 ||
            (dx >= 2 && dx <= 4 && dy >= 2 && dy <= 4));
        set(ox + dx, oy + dy, inRing ? 1 : 0);
      }
    }
  }

  // timing
  for (let i = 8; i < n - 8; i++) {
    set(i, 6, i % 2 === 0 ? 1 : 0);
    set(6, i, i % 2 === 0 ? 1 : 0);
  }

  // alignment
  const pos = ALIGN_POS[version]!;
  for (const cy of pos) {
    for (const cx of pos) {
      const nearFinder =
        (cx <= 8 && cy <= 8) || (cx >= n - 9 && cy <= 8) || (cx <= 8 && cy >= n - 9);
      if (nearFinder) continue;
      for (let dy = -2; dy <= 2; dy++) {
        for (let dx = -2; dx <= 2; dx++) {
          const on = Math.max(Math.abs(dx), Math.abs(dy)) !== 1;
          set(cx + dx, cy + dy, on ? 1 : 0);
        }
      }
    }
  }

  set(8, n - 8, 1); // dark module

  // จองพื้นที่ format info ไว้ก่อน (เขียนค่าจริงทีหลัง)
  for (let i = 0; i < 9; i++) {
    if (g.mod[idx(g, i, 8)] === -1) set(i, 8, 0);
    if (g.mod[idx(g, 8, i)] === -1) set(8, i, 0);
  }
  for (let i = 0; i < 8; i++) {
    if (g.mod[idx(g, n - 1 - i, 8)] === -1) set(n - 1 - i, 8, 0);
    if (g.mod[idx(g, 8, n - 1 - i)] === -1) set(8, n - 1 - i, 0);
  }
}

const MASKS: ((x: number, y: number) => boolean)[] = [
  (x, y) => (x + y) % 2 === 0,
  (_x, y) => y % 2 === 0,
  (x) => x % 3 === 0,
  (x, y) => (x + y) % 3 === 0,
  (x, y) => (Math.floor(y / 2) + Math.floor(x / 3)) % 2 === 0,
  (x, y) => ((x * y) % 2) + ((x * y) % 3) === 0,
  (x, y) => (((x * y) % 2) + ((x * y) % 3)) % 2 === 0,
  (x, y) => (((x + y) % 2) + ((x * y) % 3)) % 2 === 0,
];

function placeData(g: Grid, reserved: Uint8Array, bytes: Uint8Array, mask: number): void {
  const n = g.size;
  let bit = 0;
  const total = bytes.length * 8;
  let up = true;

  for (let right = n - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5; // คอลัมน์ timing ข้าม
    for (let i = 0; i < n; i++) {
      const y = up ? n - 1 - i : i;
      for (const x of [right, right - 1]) {
        if (reserved[idx(g, x, y)] === 1) continue;
        let v = 0;
        if (bit < total) {
          v = (bytes[bit >> 3]! >> (7 - (bit & 7))) & 1;
          bit++;
        }
        if (MASKS[mask]!(x, y)) v ^= 1;
        g.mod[idx(g, x, y)] = v as 0 | 1;
      }
    }
    up = !up;
  }
}

function placeFormat(g: Grid, mask: number): void {
  const n = g.size;
  // ECC M = 0b00
  let fmt = (0b00 << 3) | mask;
  let rem = fmt;
  for (let i = 0; i < 10; i++) rem = (rem << 1) ^ ((rem >> 9) * 0x537);
  fmt = ((fmt << 10) | rem) ^ 0x5412;

  for (let i = 0; i < 15; i++) {
    const b = ((fmt >> i) & 1) as 0 | 1;
    // สำเนาที่ 1 — รอบ finder ซ้ายบน
    if (i < 6) g.mod[idx(g, 8, i)] = b;
    else if (i < 8) g.mod[idx(g, 8, i + 1)] = b;
    else if (i === 8) g.mod[idx(g, 7, 8)] = b;
    else g.mod[idx(g, 14 - i, 8)] = b;
    // สำเนาที่ 2 — กันอ่านไม่ออกถ้ามุมใดมุมหนึ่งเสีย
    if (i < 8) g.mod[idx(g, n - 1 - i, 8)] = b;
    else g.mod[idx(g, 8, n - 15 + i)] = b;
  }
}

/** ลายที่ทำให้สแกนเนอร์เข้าใจผิดว่าเจอ finder pattern (สัดส่วน 1:1:3:1:1 + quiet 4) */
const N3_A = "10111010000";
const N3_B = "00001011101";

/**
 * คะแนนโทษ 4 ข้อตาม ISO/IEC 18004 §8.8.2 — ใช้เลือก mask ที่อ่านง่ายที่สุด
 * ต้องตรงกับสเปทเป๊ะ ไม่งั้นจะได้ mask คนละตัวกับ encoder มาตรฐาน
 * (ซึ่งยังสแกนได้ แต่ทำให้เทียบกับ segno เพื่อพิสูจน์ความถูกต้องไม่ได้)
 */
function penalty(g: Grid): number {
  const n = g.size;
  const at = (x: number, y: number) => g.mod[idx(g, x, y)]!;
  let score = 0;

  const scanLine = (get: (i: number) => number): void => {
    let line = "";
    let run = 1;
    for (let i = 1; i <= n; i++) {
      if (i < n && get(i) === get(i - 1)) {
        run++;
      } else {
        // N1 — แถวยาวสีเดียวกันตั้งแต่ 5 ขึ้นไป
        if (run >= 5) score += 3 + (run - 5);
        run = 1;
      }
    }
    for (let i = 0; i < n; i++) line += get(i);
    // N3 — ลายคล้าย finder ทำให้สแกนเนอร์วางกรอบผิดตำแหน่ง
    for (let i = 0; i + 11 <= n; i++) {
      const w = line.slice(i, i + 11);
      if (w === N3_A || w === N3_B) score += 40;
    }
  };

  for (let y = 0; y < n; y++) scanLine((x) => at(x, y));
  for (let x = 0; x < n; x++) scanLine((y) => at(x, y));

  // N2 — บล็อก 2×2 สีเดียว
  for (let y = 0; y < n - 1; y++) {
    for (let x = 0; x < n - 1; x++) {
      const v = at(x, y);
      if (at(x + 1, y) === v && at(x, y + 1) === v && at(x + 1, y + 1) === v) score += 3;
    }
  }

  // N4 — สัดส่วนดำ/ขาวเบี้ยวไปจาก 50%
  let dark = 0;
  for (let i = 0; i < g.mod.length; i++) if (g.mod[i] === 1) dark++;
  const total = n * n;
  const k = Math.ceil(Math.abs(dark * 20 - total * 10) / total) - 1;
  score += Math.max(0, k) * 10;
  return score;
}

/**
 * @param forceMask ระบุ mask ตรงๆ (ใช้ใน test เพื่อแยกปัญหา "เลือก mask"
 *                  ออกจากปัญหา "วางข้อมูล") ปกติปล่อยว่างให้เลือกตามคะแนนโทษ
 * @returns เมทริกซ์ boolean — true = โมดูลดำ
 */
export function encodeQR(text: string, forceMask?: number): boolean[][] {
  const bytes = new TextEncoder().encode(text);
  const version = pickVersion(bytes.length);
  const size = 17 + version * 4;

  const codewords = interleave(buildData(text, version), version);

  const base: Grid = { size, mod: new Int8Array(size * size).fill(-1) };
  placeFunctionPatterns(base, version);
  const reserved = new Uint8Array(size * size);
  for (let i = 0; i < reserved.length; i++) reserved[i] = base.mod[i] === -1 ? 0 : 1;

  let best: Grid | null = null;
  let bestScore = Infinity;
  const candidates = forceMask === undefined ? [0, 1, 2, 3, 4, 5, 6, 7] : [forceMask];
  for (const mask of candidates) {
    const g: Grid = { size, mod: Int8Array.from(base.mod) };
    placeData(g, reserved, codewords, mask);
    placeFormat(g, mask);
    const s = penalty(g);
    if (s < bestScore) {
      bestScore = s;
      best = g;
    }
  }

  const g = best!;
  const out: boolean[][] = [];
  for (let y = 0; y < size; y++) {
    const row: boolean[] = [];
    for (let x = 0; x < size; x++) row.push(g.mod[idx(g, x, y)] === 1);
    out.push(row);
  }
  return out;
}

/** วาดเป็น SVG — คมทุกขนาดจอ ไม่ต้องกังวลเรื่อง upscale */
export function qrToSvg(text: string, opts: { quiet?: number; fg?: string; bg?: string } = {}): string {
  const m = encodeQR(text);
  const q = opts.quiet ?? 4;
  const n = m.length + q * 2;
  const parts: string[] = [];
  for (let y = 0; y < m.length; y++) {
    for (let x = 0; x < m.length; x++) {
      if (m[y]![x]) parts.push(`M${x + q} ${y + q}h1v1h-1z`);
    }
  }
  return [
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${n} ${n}" shape-rendering="crispEdges">`,
    `<rect width="${n}" height="${n}" fill="${opts.bg ?? "#ffffff"}"/>`,
    `<path d="${parts.join("")}" fill="${opts.fg ?? "#000000"}"/>`,
    `</svg>`,
  ].join("");
}
